import z, { RefinementCtx } from 'zod';
import { isBefore } from 'date-fns';
import { LeaseType } from '@acp/shared';
import { MAX_TENANCY_NOTES_LENGTH } from './contact-number-schema';
import { occupantSchema } from './occupant-schema';

enum FormType {
    Add = 'add',
    Edit = 'edit'
}

function validateContactMethod(
    occupants: z.infer<typeof occupantSchema>[],
    ctx: RefinementCtx
) {
    occupants.forEach((_occ, occIdx) => {
        const hasEmailAddress = _occ.email !== null && _occ.email !== '';
        const hasContactNumber = _occ.contactNumbers.length > 0
            && _occ.contactNumbers.filter((contact) => contact.number === '').length === 0;

        if (!hasEmailAddress && !hasContactNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please enter at least one valid contact number or email',
                path: [`occupants.${occIdx}.email`],
            });
        }
    });
}

const tenancySchema = z.discriminatedUnion('formType', [
    z.object({
        formType: z.literal(FormType.Edit),
        tenancyId: z.number().int().positive(),
        startDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        endDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        renewalDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable().optional(),
        vacateDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        notes: z.string().max(MAX_TENANCY_NOTES_LENGTH, `Message should be ${MAX_TENANCY_NOTES_LENGTH} characters or less`),
    }),
    z.object({
        formType: z.literal(FormType.Add),
        tenancyId: z.null(),
        leaseType: z.nativeEnum(LeaseType),
        startDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        endDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        renewalDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable().optional(),
        vacateDate: z.date({
            invalid_type_error: 'Invalid format. Must be dd/mm/yyyy',
        }).nullable(),
        notes: z.string().max(MAX_TENANCY_NOTES_LENGTH, `Message should be ${MAX_TENANCY_NOTES_LENGTH} characters or less`),
    }),
]);

const formSchema = z.object({
    propertyId: z.coerce.number().int().positive().gt(0)
        .finite(),
    tenancy: tenancySchema.refine((tenancy) => {
        if (tenancy.endDate !== null && tenancy.startDate !== null) {
            return isBefore(tenancy.startDate, tenancy.endDate);
        }
        return true;
    }, {
        message: 'Tenancy end date cannot be on or before the start date',
        path: ['endDate'],
    }).refine((tenancy) => {
        if (tenancy.vacateDate !== null && tenancy.startDate !== null) {
            return isBefore(tenancy.startDate, tenancy.vacateDate);
        }
        return true;
    }, {
        message: 'Vacate date cannot be on or before the start date',
        path: ['vacateDate'],
    }),
    occupants: z.array(occupantSchema),
}).superRefine((vals, ctx) => {
    validateContactMethod(vals.occupants, ctx);
});

type FormValues = z.infer<typeof formSchema>;

export {
    formSchema,
    FormValues,
    FormType,
};
